import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { computeStage, activeScreens } from "./boardSchema";
import ScreenRenderer from "./ScreenRenderer";
import "./board.scss";

function useViewport() {
  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return vp;
}

// Renders a board config: rotates through its screens, each shown for its
// configured duration, on a centered 16:9 stage. `forceScreenId` (used by the
// alarm takeover and by Studio previews) pins a specific screen and pauses
// rotation. Takes the config as props so a preview can render one without a
// signed-in board.
export const BoardView = ({ boardConfig, agency, forceScreenId = null }) => {
  const vp = useViewport();
  const stage = useMemo(() => computeStage(vp.w, vp.h), [vp.w, vp.h]);
  const screens = useMemo(() => activeScreens(boardConfig), [boardConfig]);
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  // Keep the index in range as screens change.
  useEffect(() => {
    if (index >= screens.length && screens.length > 0) setIndex(0);
  }, [screens.length, index]);

  // Rotate, unless a screen is forced.
  useEffect(() => {
    if (forceScreenId || screens.length <= 1) return undefined;
    const current = screens[index] || screens[0];
    const duration = Math.max(5000, current?.durationMs || 30000);
    timerRef.current = setTimeout(() => {
      setIndex((i) => (i + 1) % screens.length);
    }, duration);
    return () => clearTimeout(timerRef.current);
  }, [index, screens, forceScreenId]);

  const background = boardConfig?.theme?.background || "#0b0b0d";

  if (!boardConfig) {
    return (
      <div className="sb-stage-bg" style={{ background }}>
        <div className="sb-board-waiting">Waiting for board configuration…</div>
      </div>
    );
  }
  if (screens.length === 0) {
    return (
      <div className="sb-stage-bg" style={{ background }}>
        <div className="sb-board-waiting">
          This board has no screens yet. Add some in the Board Designer.
        </div>
      </div>
    );
  }

  const forced = forceScreenId ? screens.find((s) => s.id === forceScreenId) : null;
  const screen = forced || screens[Math.min(index, screens.length - 1)];

  return (
    <div className="sb-stage-bg" style={{ background }}>
      <ScreenRenderer screen={screen} stage={stage} agency={agency} board={boardConfig} />
    </div>
  );
};

// The signed-in board's own config.
const BoardRenderer = ({ forceScreenId = null }) => {
  const { boardConfig, agency } = useAuth();
  return <BoardView boardConfig={boardConfig} agency={agency} forceScreenId={forceScreenId} />;
};

export default BoardRenderer;
